import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faTimes, faCheckCircle, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useCaja } from './CajaContext';
import { useData } from '../DataContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import moneda, { formatearMoneda, parsearMoneda } from '../moneda';
import jsPDF from 'jspdf';
import storage from '../storage';

const parseValor = (v) => {
    const p = parsearMoneda(v || '');
    return (p === '' || isNaN(p)) ? 0 : Number(p);
};

export default function CerrarCajaModal({ onClose }) {
    const { caja, cerrarCaja } = useCaja();
    const { metodos } = useData();
    const [montoFinal, setMontoFinal] = useState('');
    const [nota, setNota] = useState('');
    const [montosPorMetodo, setMontosPorMetodo] = useState({});
    const [enviando, setEnviando] = useState(false);

    const fmt = (n) => Number(n || 0).toLocaleString('es-CO');

    const montoEsperado = useMemo(() => {
        if (!caja) return 0;
        return Number(caja.montoInicial || 0) + Number(caja.ventasEfectivo || 0);
    }, [caja]);

    const diferencia = useMemo(() => {
        const montoLimpio = parsearMoneda(montoFinal);
        if (montoLimpio === '' || isNaN(montoLimpio)) return null;
        return Number(montoLimpio) - montoEsperado;
    }, [montoFinal, montoEsperado]);

    const esperadoPorMetodo = useMemo(() => {
        const map = {};
        (caja?.ventasPorMetodo || []).forEach(e => { map[e.metodo] = Number(e.monto || 0); });
        return map;
    }, [caja]);

    const totalTransferencia = (metodos || []).reduce((s, m) => s + parseValor(montosPorMetodo[m.nombre]), 0);
    const diferenciaTransferencia = totalTransferencia - Number(caja?.ventasTransferencia || 0);

    const setMontoMetodo = (nombre, valor) => {
        setMontosPorMetodo(prev => ({ ...prev, [nombre]: formatearMoneda(valor) }));
    };

    const handleCerrar = async (e) => {
        e.preventDefault();
        const montoLimpio = parsearMoneda(montoFinal);
        if (montoLimpio === '' || isNaN(montoLimpio)) {
            toast.error('Ingresá el monto final en caja');
            return;
        }
        setEnviando(true);

        const transferenciasPorMetodo = {};
        (metodos || []).forEach(m => { transferenciasPorMetodo[m.nombre] = parseValor(montosPorMetodo[m.nombre]); });

        const res = await cerrarCaja(Number(montoLimpio), nota, totalTransferencia, transferenciasPorMetodo);
        setEnviando(false);

        if (res.error) {
            toast.error(res.error);
        } else {
            toast.success('Caja cerrada correctamente');
            const usuario = JSON.parse(storage.getItem('adminUser') || '{}');
            generarPDFCierre({
                idCaja: caja.idCaja,
                fecha: new Date().toLocaleString('es-CO'),
                trabajador: usuario.nombre || '',
                montoInicial: caja.montoInicial,
                ventasEfectivo: res.ventasEfectivo,
                ventasTransferencia: res.ventasTransferencia,
                montoFinal: Number(montoLimpio),
                transferenciasPorMetodo,
                esperadoPorMetodo,
                diferencia: res.diferencia,
                diferenciaTransferencia: res.diferenciaTransferencia,
                nota: nota,
            });
            setTimeout(onClose, 800);
        }
    };

    const generarPDFCierre = (datos) => {
        const ANCHO = 80;
        const ALTO = 240;
        const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: [ANCHO, ALTO] });
        const margin = 4;
        const W = ANCHO - margin * 2;
        let y = 6;

        const checkPage = (needed) => {
            if (y + needed > ALTO - 6) {
                pdf.addPage([ANCHO, ALTO]);
                y = 6;
            }
        };
        const line = () => {
            checkPage(4);
            pdf.setDrawColor(0);
            pdf.setLineWidth(0.1);
            pdf.line(margin, y, ANCHO - margin, y);
            y += 3;
        };
        const fila = (label, valor, negrita = false) => {
            checkPage(4);
            pdf.setFont('helvetica', negrita ? 'bold' : 'normal');
            pdf.text(label, margin, y);
            pdf.text(String(valor), ANCHO - margin, y, { align: 'right' });
            y += 4;
        };

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(13);
        pdf.text('CIERRE DE CAJA', ANCHO / 2, y, { align: 'center' });
        y += 5;
        pdf.setFontSize(9);
        pdf.text(`Caja N° ${datos.idCaja}`, ANCHO / 2, y, { align: 'center' });
        y += 4;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.text(String(datos.fecha || ''), ANCHO / 2, y, { align: 'center' });
        y += 5;

        line();
        pdf.setFontSize(8);
        fila('Trabajador:', datos.trabajador || '—');
        line();

        fila('Monto inicial', `${moneda} ${fmt(datos.montoInicial)}`);
        fila('Ventas efectivo', `${moneda} ${fmt(datos.ventasEfectivo)}`);
        fila('Ventas transferencia', `${moneda} ${fmt(datos.ventasTransferencia)}`);
        line();

        fila('Efectivo en caja', `${moneda} ${fmt(datos.montoFinal)}`);

        // Desglose por método
        const clavesMetodo = Object.keys(datos.transferenciasPorMetodo || {});
        if (clavesMetodo.length > 0) {
            clavesMetodo.forEach(k => {
                fila(`  ${k}`, `${moneda} ${fmt(datos.transferenciasPorMetodo[k])}`);
            });
        }

        line();

        if (datos.diferencia != null) {
            const diff = Number(datos.diferencia);
            fila('Dif. efectivo', diff === 0 ? 'Cuadrada' : diff > 0 ? `+${moneda} ${fmt(diff)}` : `-${moneda} ${fmt(Math.abs(diff))}`);
        }
        if (datos.diferenciaTransferencia != null) {
            const dtf = Number(datos.diferenciaTransferencia);
            fila('Dif. transf.', dtf === 0 ? 'Cuadrada' : dtf > 0 ? `+${moneda} ${fmt(dtf)}` : `-${moneda} ${fmt(Math.abs(dtf))}`);
        }

        line();

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        const totalFinal = Number(datos.montoFinal || 0) + Number(datos.transferenciasPorMetodo
            ? Object.values(datos.transferenciasPorMetodo).reduce((s, v) => s + Number(v || 0), 0)
            : 0);
        fila('TOTAL FINAL', `${moneda} ${fmt(totalFinal)}`, true);

        if (datos.nota) {
            checkPage(4);
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(7);
            pdf.text(`Nota: ${datos.nota}`, margin, y, { maxWidth: W });
            y += 4;
        }

        line();
        pdf.setFontSize(7);
        pdf.text('Cierre de caja generado', ANCHO / 2, y, { align: 'center' });

        pdf.save(`cierre_caja_${datos.idCaja}.pdf`);
    };

    if (!caja) return null;

    return (
        <div style={{
            position: 'fixed', inset: 0,
            background: 'rgba(15, 20, 35, 0.85)',
            zIndex: 99999,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px',
        }}>
            <ToastContainer />
            <div style={{
                background: '#fff', borderRadius: '14px',
                padding: '28px', maxWidth: '480px', width: '100%',
                boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                maxHeight: '90vh', overflowY: 'auto',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <h2 style={{ margin: 0, fontSize: '20px', color: '#212529' }}>
                        <FontAwesomeIcon icon={faLock} style={{ color: '#dc3545', marginRight: '8px' }} />
                        Cerrar caja
                    </h2>
                    <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', color: '#6c757d', cursor: 'pointer' }}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '14px', marginBottom: '16px', fontSize: '14px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                        <span>Monto inicial</span>
                        <strong>{moneda} {fmt(caja.montoInicial)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                        <span>Ventas en efectivo</span>
                        <strong style={{ color: '#28a745' }}>+ {moneda} {fmt(caja.ventasEfectivo)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderTop: '1px solid #dee2e6', marginTop: '4px', paddingTop: '8px' }}>
                        <strong>Monto esperado</strong>
                        <strong style={{ fontSize: '16px' }}>{moneda} {fmt(montoEsperado)}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px', color: '#6c757d' }}>
                        <span>Ventas por transferencia (esperado)</span>
                        <span>{moneda} {fmt(caja.ventasTransferencia)}</span>
                    </div>
                </div>

                {caja.ventasPorTrabajador && caja.ventasPorTrabajador.length > 0 && (
                    <div style={{ background: '#fff', border: '1px solid #e9ecef', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                        <div style={{ fontSize: '12px', fontWeight: 700, color: '#495057', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
                            Ventas por trabajador
                        </div>
                        <table style={{ width: '100%', fontSize: '13px', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: '#f8f9fa' }}>
                                    <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 600, color: '#495057' }}>Trabajador</th>
                                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: '#28a745' }}>Efectivo</th>
                                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: '#0d6efd' }}>Transf.</th>
                                    <th style={{ textAlign: 'right', padding: '6px 8px', fontWeight: 600, color: '#495057' }}>#</th>
                                </tr>
                            </thead>
                            <tbody>
                                {caja.ventasPorTrabajador.map((t, i) => (
                                    <tr key={i} style={{ borderTop: '1px solid #e9ecef' }}>
                                        <td style={{ padding: '6px 8px' }}>{t.nombre || '—'}</td>
                                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{moneda} {fmt(t.efectivo)}</td>
                                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{moneda} {fmt(t.transferencia)}</td>
                                        <td style={{ padding: '6px 8px', textAlign: 'right' }}>{t.totalPedidos}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <form onSubmit={handleCerrar}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#495057', marginBottom: '6px' }}>
                        Monto final contado en caja ({moneda})
                    </label>
                    <input
                        type='text'
                        inputMode='numeric'
                        value={montoFinal}
                        onChange={(e) => setMontoFinal(formatearMoneda(e.target.value))}
                        placeholder='0'
                        autoFocus
                        style={{
                            width: '100%', padding: '12px 14px', fontSize: '18px',
                            border: '2px solid #e9ecef', borderRadius: '8px',
                            outline: 'none', textAlign: 'center',
                            boxSizing: 'border-box', marginBottom: '12px',
                        }}
                    />

                    {diferencia !== null && (
                        <div style={{
                            padding: '10px 14px', borderRadius: '8px',
                            marginBottom: '12px',
                            background: diferencia === 0 ? '#e7f7ee' : diferencia > 0 ? '#fff8e1' : '#fde8e9',
                            color: diferencia === 0 ? '#155724' : diferencia > 0 ? '#7a5b00' : '#721c24',
                            fontSize: '14px',
                            display: 'flex', alignItems: 'center', gap: '8px',
                        }}>
                            <FontAwesomeIcon icon={diferencia === 0 ? faCheckCircle : faExclamationTriangle} />
                            {diferencia === 0 ? 'Caja cuadrada' : diferencia > 0 ? `Sobrante: ${moneda} ${fmt(diferencia)}` : `Faltante: ${moneda} ${fmt(Math.abs(diferencia))}`}
                        </div>
                    )}

                    {metodos.length > 0 && (
                        <div style={{ marginBottom: '12px' }}>
                            <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#495057', marginBottom: '6px' }}>
                                Montos en cuentas por método ({moneda})
                            </label>
                            {metodos.map(m => {
                                const esperado = esperadoPorMetodo[m.nombre] || 0;
                                const valor = montosPorMetodo[m.nombre] || '';
                                const declarado = parseValor(valor);
                                const dif = declarado - esperado;
                                return (
                                    <div key={m.idMetodo} style={{ marginBottom: '10px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
                                            <span style={{ fontWeight: 600, color: '#495057' }}>{m.nombre}</span>
                                            <span style={{ color: '#6c757d' }}>Esperado: {moneda} {fmt(esperado)}</span>
                                        </div>
                                        <input
                                            type='text'
                                            inputMode='numeric'
                                            value={valor}
                                            onChange={(e) => setMontoMetodo(m.nombre, e.target.value)}
                                            placeholder='0'
                                            style={{
                                                width: '100%', padding: '10px 14px', fontSize: '16px',
                                                border: '2px solid #e9ecef', borderRadius: '8px',
                                                outline: 'none', textAlign: 'center', boxSizing: 'border-box',
                                            }}
                                        />
                                        {valor !== '' && (
                                            <div style={{
                                                fontSize: '13px', marginTop: '4px',
                                                color: dif === 0 ? '#155724' : dif > 0 ? '#7a5b00' : '#721c24',
                                            }}>
                                                {dif === 0 ? 'Cuadrado' : dif > 0 ? `Sobrante: ${moneda} ${fmt(dif)}` : `Faltante: ${moneda} ${fmt(Math.abs(dif))}`}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #dee2e6', fontSize: '14px', fontWeight: 700 }}>
                                <span>Total transferencias</span>
                                <span>{moneda} {fmt(totalTransferencia)}</span>
                            </div>
                            <div style={{
                                fontSize: '13px', marginTop: '4px',
                                color: diferenciaTransferencia === 0 ? '#155724' : diferenciaTransferencia > 0 ? '#7a5b00' : '#721c24',
                            }}>
                                {diferenciaTransferencia === 0 ? 'Transferencias cuadradas' : diferenciaTransferencia > 0 ? `Sobrante transf.: ${moneda} ${fmt(diferenciaTransferencia)}` : `Faltante transf.: ${moneda} ${fmt(Math.abs(diferenciaTransferencia))}`}
                            </div>
                        </div>
                    )}

                    <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#495057', marginBottom: '6px' }}>
                        Nota (opcional)
                    </label>
                    <textarea
                        value={nota}
                        onChange={(e) => setNota(e.target.value)}
                        placeholder='Observaciones del cierre...'
                        rows={2}
                        style={{
                            width: '100%', padding: '10px 14px', fontSize: '14px',
                            border: '2px solid #e9ecef', borderRadius: '8px',
                            outline: 'none', resize: 'vertical',
                            boxSizing: 'border-box', marginBottom: '16px',
                            fontFamily: 'inherit',
                        }}
                    />

                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                            type='button'
                            onClick={onClose}
                            style={{
                                flex: 1, padding: '12px',
                                background: '#e9ecef', color: '#495057',
                                border: 'none', borderRadius: '8px',
                                fontSize: '14px', fontWeight: 600, cursor: 'pointer',
                            }}
                        >
                            Cancelar
                        </button>
                        <button
                            type='submit'
                            disabled={enviando}
                            style={{
                                flex: 2, padding: '12px',
                                background: enviando ? '#6c757d' : '#dc3545',
                                color: '#fff', border: 'none', borderRadius: '8px',
                                fontSize: '14px', fontWeight: 700,
                                cursor: enviando ? 'wait' : 'pointer',
                            }}
                        >
                            {enviando ? 'Cerrando...' : 'Cerrar caja'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
